<script setup>
// SiteUp (auditoria UX — onboarding): checklist de "próximos passos" no topo das
// Configurações do assistente. O cliente caía direto nas configs sem saber a
// ordem nem que precisa CONECTAR UMA CAIXA pra IA atender. Aqui mostramos os
// passos essenciais (com status feito/pendente) e os recomendados, cada um com
// atalho. Só leitura — não muda nada, só orienta.
import { computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore, useMapGetter } from 'dashboard/composables/store';

const props = defineProps({
  assistant: {
    type: Object,
    default: () => ({}),
  },
});

const route = useRoute();
const router = useRouter();
const store = useStore();

const captainInboxes = useMapGetter('captainInboxes/getRecords');

const assistantId = computed(() => Number(route.params.assistantId));

onMounted(() => {
  if (assistantId.value) {
    store
      .dispatch('captainInboxes/get', { assistantId: assistantId.value })
      .catch(() => {});
  }
});

const cfg = computed(() => props.assistant?.config || {});

const hasIdentity = computed(
  () =>
    !!props.assistant?.name &&
    !!props.assistant?.description &&
    !!cfg.value.product_name
);
const hasInstructions = computed(() => !!(cfg.value.instructions || '').trim());
const hasInbox = computed(() => (captainInboxes.value || []).length > 0);
const hasActiveInbox = computed(() =>
  (captainInboxes.value || []).some(
    i => i.captain_ai_mode && i.captain_ai_mode !== 'off'
  )
);

const go = name =>
  router.push({
    name,
    params: { accountId: route.params.accountId, assistantId: assistantId.value },
  });

// Passos essenciais (com status) — a ORDEM é o caminho recomendado.
const steps = computed(() => [
  {
    key: 'identity',
    label: 'Identidade: nome, produto e descrição',
    done: hasIdentity.value,
    action: null, // já está nesta página (Configurações básicas)
  },
  {
    key: 'instructions',
    label: 'Instruções: o prompt principal (personalidade e regras)',
    done: hasInstructions.value,
    action: null, // nesta página (Configurações do sistema)
  },
  {
    key: 'inbox',
    label: 'Conectar uma caixa de entrada e escolher o modo da IA',
    done: hasInbox.value && hasActiveInbox.value,
    hint:
      hasInbox.value && !hasActiveInbox.value
        ? 'Caixa conectada, mas a IA está desligada nela.'
        : '',
    action: { label: 'Conectar caixa', route: 'captain_assistants_inboxes_index' },
  },
]);

const recommended = [
  {
    label: 'Treinar conhecimento (FAQs e documentos)',
    route: 'captain_assistants_responses_index',
  },
  {
    label: 'Refinar comportamento (cenários, diretrizes, proteções)',
    route: 'captain_assistants_guidelines_index',
  },
  {
    label: 'Testar no Playground antes de soltar',
    route: 'captain_assistants_playground_index',
  },
];

const doneCount = computed(() => steps.value.filter(s => s.done).length);
const allDone = computed(() => doneCount.value === steps.value.length);
</script>

<template>
  <div class="flex flex-col gap-3 rounded-2xl border border-n-weak p-4">
    <div class="flex items-center justify-between gap-3">
      <div class="flex items-center gap-2">
        <span
          :class="
            allDone ? 'i-ri-checkbox-circle-line text-n-teal-10' : 'i-ri-list-check text-n-brand'
          "
          class="size-5 shrink-0"
        />
        <h6 class="text-n-slate-12 text-base font-medium">
          {{ allDone ? 'Agente pronto para atender' : 'Próximos passos para colocar o agente no ar' }}
        </h6>
      </div>
      <span class="text-xs text-n-slate-11 shrink-0">
        {{ doneCount }}/{{ steps.length }} essenciais
      </span>
    </div>

    <ul class="flex flex-col gap-2">
      <li
        v-for="step in steps"
        :key="step.key"
        class="flex items-center justify-between gap-3"
      >
        <div class="flex items-start gap-2 min-w-0">
          <span
            :class="
              step.done
                ? 'i-ri-checkbox-circle-line text-n-teal-10'
                : 'i-ri-checkbox-blank-circle-line text-n-slate-9'
            "
            class="size-4 shrink-0 mt-0.5"
          />
          <div class="flex flex-col min-w-0">
            <span
              class="text-sm"
              :class="step.done ? 'text-n-slate-11 line-through' : 'text-n-slate-12'"
            >
              {{ step.label }}
            </span>
            <span v-if="step.hint" class="text-xs text-n-amber-11">
              {{ step.hint }}
            </span>
          </div>
        </div>
        <button
          v-if="step.action && !step.done"
          type="button"
          class="shrink-0 text-xs font-medium text-n-brand hover:underline"
          @click="go(step.action.route)"
        >
          {{ step.action.label }}
        </button>
      </li>
    </ul>

    <div class="flex flex-col gap-1 pt-2 border-t border-n-weak">
      <span class="text-xs font-medium text-n-slate-11">Recomendado</span>
      <button
        v-for="rec in recommended"
        :key="rec.route"
        type="button"
        class="flex items-center gap-2 text-left text-sm text-n-slate-11 hover:text-n-slate-12"
        @click="go(rec.route)"
      >
        <span class="i-ri-arrow-right-line size-3.5 shrink-0 text-n-slate-9" />
        {{ rec.label }}
      </button>
    </div>
  </div>
</template>
